// A copy of the drawing for the undo stack: the drawing serialized, with its heavy
// payloads HELD instead of copied into the text. The heavy payloads are the
// imported DXF plan (`background`, every entity of it) and every long string (a
// PDF underlay's bytes, an image's or a logo's data URL). None of them changes in
// place: an import replaces the plan whole, and a string cannot change. So every
// step that has one shares the one object, and a step costs what the model costs,
// not what the plan under it costs (a 40 000-line plan and a 4 MB PDF made each
// step 9 MB and 54 ms; held, they cost a reference).
// DOM-free (test/drawingSnapshot.test.mjs).

/** The drawing as it was: its text, and the payloads the text refers to by index. */
export interface DrawingSnapshot {
  readonly json: string;
  readonly held: readonly unknown[];
}

/** A string at least this long is held, not copied. */
export const HELD_STRING = 8192;
const REF = '\u0000held:';

/** The drawing now, as a snapshot (see the file's head for what is held). */
export function snapshotOf(drawing: object): DrawingSnapshot {
  const held: unknown[] = [];
  const index = new Map<unknown, number>();
  const hold = (v: unknown): string => {
    let i = index.get(v);
    if (i === undefined) { i = held.length; held.push(v); index.set(v, i); }
    return REF + i;
  };
  const json = JSON.stringify(drawing, function (this: unknown, key: string, v: unknown) {
    if (typeof v === 'string') return v.length >= HELD_STRING ? hold(v) : v;
    if (this === drawing && key === 'background' && v && typeof v === 'object') return hold(v);
    return v;
  });
  return { json, held };
}

/** A new drawing object as the snapshot has it; its held payloads are the ones
 *  the snapshot holds (the same objects, never copies). */
export function drawingOf<D>(s: DrawingSnapshot): D {
  if (!s.held.length) return JSON.parse(s.json) as D;
  return JSON.parse(s.json, (_k, v: unknown) => (typeof v === 'string' && v.startsWith(REF) ? s.held[Number(v.slice(REF.length))] : v)) as D;
}

/** Whether two snapshots are of the same drawing. */
export function sameSnapshot(a: DrawingSnapshot, b: DrawingSnapshot): boolean {
  return a.json === b.json && a.held.length === b.held.length && a.held.every((v, i) => v === b.held[i]);
}
